/* CPAP Mask Finder — controller (ported from Claude Design React prototype to a vanilla custom element).
   Data is rendered server-side by sections/cpap-mask-finder.liquid into #<id>-data.
   Add-to-cart uses Horizon's cart: POST cart/add.js then dispatch CartAddEvent (drawer auto-opens, cart re-renders). */

import { CartAddEvent } from '@theme/events';

const STEPS = ['Start', 'Machine', 'Mask type', 'Fit', 'Matches'];
const MASK_TYPES = ['Full Face', 'Nasal', 'Nasal Pillow'];

/* ---------- helpers ---------- */
const esc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const money = (n) => '$' + Number(n || 0).toLocaleString('en-AU');

const ICON = {
  check: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="3,8.5 6.5,12 13,4.5"/></svg>',
  checkSmall: '<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="2.5,6.5 5,9 9.5,3.5"/></svg>',
  chevLeft: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="10,3 5,8 10,13"/></svg>',
  chevRight: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,3 11,8 6,13"/></svg>',
  arrowRight: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="10" x2="17" y2="10"/><polyline points="12,5 17,10 12,15"/></svg>',
  close: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>',
  truck: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="5" width="11" height="8" rx="1"/><path d="M12 7h3.5l2.5 3v3H12z"/><circle cx="5" cy="15" r="1.6"/><circle cx="15" cy="15" r="1.6"/></svg>',
  shield: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2 L17 4 V10 C17 14 13.5 16.5 10 18 C6.5 16.5 3 14 3 10 V4 Z"/><polyline points="7,10 9,12 13,8"/></svg>',
  starOutline: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><polygon points="10,2 12.5,7.3 18,8.1 14,12 15,17.5 10,14.8 5,17.5 6,12 2,8.1 7.5,7.3"/></svg>',
  sparkle: '<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor"><path d="M10 2 L11.2 7.4 L16.5 8.5 L11.2 9.6 L10 15 L8.8 9.6 L3.5 8.5 L8.8 7.4 Z"/><circle cx="16" cy="3.5" r="1"/><circle cx="4" cy="16" r="1"/></svg>',
  bundle: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M3 7 L10 4 L17 7 V13 L10 16 L3 13 Z"/><path d="M3 7 L10 10 L17 7"/><path d="M10 10 V16"/></svg>',
  tag: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"><path d="M3 3 L10 3 L17 10 L10 17 L3 10 Z"/><circle cx="7" cy="7" r="1.2"/></svg>',
  cart: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3 H4 L6 13 H15 L17 6 H6"/><circle cx="7" cy="16" r="1.2"/><circle cx="14" cy="16" r="1.2"/></svg>',
  help: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="7.5"/><path d="M7.5 7.5 C7.5 6 8.7 5 10 5 C11.3 5 12.5 6 12.5 7.5 C12.5 9 10 9.2 10 11"/><line x1="10" y1="13.5" x2="10" y2="14" stroke-width="2"/></svg>',
  alert: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2 L18 16 H2 Z"/><line x1="10" y1="8" x2="10" y2="12"/><line x1="10" y1="14" x2="10" y2="14.5" stroke-width="2"/></svg>',
  filter: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" y1="4" x2="14" y2="4"/><line x1="4" y1="8" x2="12" y2="8"/><line x1="6" y1="12" x2="10" y2="12"/></svg>',
  refresh: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 6 A5 5 0 1 0 14 9.5"/><polyline points="13,2 13,6 9,6"/></svg>',
};

const PLACEHOLDER_SHAPES = {
  machine: '<rect x="22" y="32" width="76" height="42" rx="6" fill="url(#mfdiag)" stroke="#cfd8de"/><rect x="32" y="40" width="34" height="22" rx="2" fill="#fff" stroke="#cfd8de"/><circle cx="84" cy="51" r="8" fill="#fff" stroke="#cfd8de"/><path d="M98 50 q12 0 14 -12" fill="none" stroke="#cfd8de" stroke-width="6" stroke-linecap="round"/>',
  fullface: '<path d="M40 28 Q60 18 80 28 Q92 40 88 60 Q80 76 60 78 Q40 76 32 60 Q28 40 40 28 Z" fill="url(#mfdiag)" stroke="#cfd8de"/><path d="M48 40 Q60 34 72 40 Q76 56 60 64 Q44 56 48 40 Z" fill="#fff" stroke="#cfd8de"/>',
  nasal: '<path d="M44 32 Q60 24 76 32 Q86 44 82 56 Q72 66 60 66 Q48 66 38 56 Q34 44 44 32 Z" fill="url(#mfdiag)" stroke="#cfd8de"/><path d="M52 40 Q60 36 68 40 Q70 50 60 54 Q50 50 52 40 Z" fill="#fff" stroke="#cfd8de"/>',
  pillow: '<path d="M48 38 Q60 32 72 38 Q76 48 72 56 Q60 60 48 56 Q44 48 48 38 Z" fill="url(#mfdiag)" stroke="#cfd8de"/><ellipse cx="55" cy="44" rx="3" ry="2.4" fill="#fff" stroke="#cfd8de"/><ellipse cx="65" cy="44" rx="3" ry="2.4" fill="#fff" stroke="#cfd8de"/>',
  accessory: '<rect x="32" y="36" width="56" height="34" rx="4" fill="url(#mfdiag)" stroke="#cfd8de"/><line x1="40" y1="46" x2="80" y2="46" stroke="#cfd8de"/><line x1="40" y1="54" x2="68" y2="54" stroke="#cfd8de"/>',
};
const kindForType = (t) => (t === 'Full Face' ? 'fullface' : t === 'Nasal' ? 'nasal' : t === 'Nasal Pillow' ? 'pillow' : 'mask');
function placeholder(kind, w = '70%') {
  return `<div class="prod-img-placeholder" style="width:${w}"><svg viewBox="0 0 120 100" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="product image"><defs><pattern id="mfdiag" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)"><rect width="6" height="6" fill="#f3f6f8"/><line x1="0" y1="0" x2="0" y2="6" stroke="#e1e9ee" stroke-width="3"/></pattern></defs>${PLACEHOLDER_SHAPES[kind] || PLACEHOLDER_SHAPES.pillow}</svg></div>`;
}
function productImg(p, kind, w) {
  if (p && p.image) return `<img src="${esc(p.image)}" alt="${esc(p.title || '')}" loading="lazy" style="max-width:${w || '78%'};max-height:100%;object-fit:contain">`;
  return placeholder(kind, w);
}

function stars(rating, reviews) {
  const r = Number(rating) || 0;
  const full = Math.floor(r);
  const half = r - full >= 0.4 && r - full < 0.9;
  let out = '<span class="stars" aria-label="' + esc(r) + ' out of 5 stars' + (reviews != null ? ', ' + reviews + ' reviews' : '') + '">';
  for (let i = 0; i < 5; i++) {
    if (i < full) out += '<svg viewBox="0 0 16 16" width="13" height="13" fill="#feb62c" stroke="#feb62c" stroke-width="1.4"><polygon points="8,1.6 10,5.7 14.4,6.3 11.2,9.4 12,13.8 8,11.7 4,13.8 4.8,9.4 1.6,6.3 6,5.7"/></svg>';
    else if (i === full && half) out += '<svg viewBox="0 0 16 16" width="13" height="13"><defs><linearGradient id="mfhalf"><stop offset="50%" stop-color="#feb62c"/><stop offset="50%" stop-color="transparent"/></linearGradient></defs><polygon points="8,1.6 10,5.7 14.4,6.3 11.2,9.4 12,13.8 8,11.7 4,13.8 4.8,9.4 1.6,6.3 6,5.7" fill="url(#mfhalf)" stroke="#feb62c" stroke-width="1.4"/></svg>';
    else out += '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="#feb62c" stroke-width="1.4"><polygon points="8,1.6 10,5.7 14.4,6.3 11.2,9.4 12,13.8 8,11.7 4,13.8 4.8,9.4 1.6,6.3 6,5.7"/></svg>';
  }
  if (reviews != null) out += '<span class="stars-meta">(' + reviews + ')</span>';
  return out + '</span>';
}

/* ---------- recommendation engine (mask TYPE) ---------- */
function recommendMaskType(answers) {
  const scores = { 'Full Face': 0, Nasal: 0, 'Nasal Pillow': 0 };
  const reasons = [];
  if (answers.breathing === 'mouth' || answers.breathing === 'dry-mouth') { scores['Full Face'] += 3; reasons.push('you breathe through your mouth or wake up dry'); }
  else if (answers.breathing === 'nose') { scores.Nasal += 2; scores['Nasal Pillow'] += 2; reasons.push('you breathe through your nose'); }
  else if (answers.breathing === 'unsure') { scores.Nasal += 1; scores['Full Face'] += 1; }
  if (answers.congestion === 'often') { scores['Full Face'] += 2; reasons.push('you often get a blocked nose'); }
  else if (answers.congestion === 'no') { scores['Nasal Pillow'] += 1; }
  if (answers.position === 'side' || answers.position === 'stomach' || answers.position === 'mix') { scores['Nasal Pillow'] += 2; if (answers.position === 'side') reasons.push('you sleep on your side'); }
  else if (answers.position === 'back') { scores['Full Face'] += 1; }
  if (answers.active === 'yes') { scores['Nasal Pillow'] += 1.5; reasons.push('you move around in your sleep'); }
  if (answers.claustro === 'yes') { scores['Nasal Pillow'] += 3; scores['Full Face'] -= 2; reasons.push("you'd prefer minimal coverage"); }
  else if (answers.claustro === 'abit') { scores['Nasal Pillow'] += 1.5; scores.Nasal += 1; }
  if (answers.readers === 'yes') { scores['Nasal Pillow'] += 1.5; scores.Nasal += 1; reasons.push('you like to read or wear glasses in bed'); }
  if (answers.pressure === 'high') { scores['Full Face'] += 2; scores['Nasal Pillow'] -= 1; reasons.push('your prescribed pressure is high'); }
  else if (answers.pressure === 'low') { scores['Nasal Pillow'] += 1; }
  if (answers.skin === 'yes') reasons.push('your skin marks easily');
  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const type = ranked[0][0];
  const alt = ranked[1][0];
  const headline = type === 'Full Face' ? 'A full face mask is your best fit.' : type === 'Nasal' ? 'A nasal mask is your best fit.' : 'A nasal pillow mask is your best fit.';
  return { type, alt, reasons, headline };
}

class CpapMaskFinder extends HTMLElement {
  connectedCallback() {
    const dataEl = document.getElementById(this.getAttribute('data-target'));
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { this.data = { machines: [], masks: [], brands: [], machineTypes: [], questions: [], accessories: [], settings: {} }; }
    this.s = {
      step: 0, owned: null, machine: null, maskType: null,
      prefs: { cushion: [], headgear: [], brand: [] }, answers: {},
      overlay: false, qStep: 0, sizes: {}, sizeError: {},
      brandFilter: [], typeFilter: [], recommendation: null,
    };
    this.classList.add('cpap-mask-finder');
    var seo = document.querySelector('[data-mfs-seo]');
    if (seo) seo.hidden = true;
    this.addEventListener('click', this.onClick.bind(this));
    this.render();
  }

  set(part) { Object.assign(this.s, part); this.render(); }

  onClick(e) {
    const t = e.target.closest('[data-action]');
    if (!t) return;
    const a = t.dataset.action;
    const d = t.dataset;
    switch (a) {
      case 'own': this.set({ owned: d.val === 'true' }); break;
      case 'next': this.goNext(); break;
      case 'back': this.set({ step: Math.max(0, this.s.step - 1) }); this.top(); break;
      case 'restart': this.restart(); break;
      case 'skip-machine': this.set({ machine: null, step: 2 }); this.top(); break;
      case 'skip-prefs': this.set({ step: 4 }); this.top(); break;
      case 'machine-select': this.selectMachine(d.id); break;
      case 'machine-add': this.selectMachine(d.id); this.addToCart(d.variantId); break;
      case 'fbrand': this.toggleArr('brandFilter', d.val); break;
      case 'ftype': this.toggleArr('typeFilter', d.val); break;
      case 'fclear': this.set({ brandFilter: [], typeFilter: [] }); break;
      case 'masktype': this.set({ maskType: d.type }); break;
      case 'open-quiz': this.set({ overlay: true, qStep: this.s.answers && Object.keys(this.s.answers).length ? this.s.qStep : 0 }); break;
      case 'close-quiz': this.set({ overlay: false }); break;
      case 'quiz-pick': { const ans = { ...this.s.answers, [d.q]: d.val }; this.s.answers = ans; this.render(); break; }
      case 'quiz-next': this.quizNext(); break;
      case 'quiz-back': this.set({ qStep: Math.max(0, this.s.qStep - 1) }); break;
      case 'quiz-finish': this.finishQuiz(); break;
      case 'pref': this.togglePref(d.key, d.val); break;
      case 'size': { this.s.sizes = { ...this.s.sizes, [d.id]: d.variantId }; this.s.sizeError = { ...this.s.sizeError, [d.id]: false }; this.render(); break; }
      case 'add-mask': this.addMask(d.id); break;
      case 'add-cart': this.addToCart(d.variantId); break;
      default: break;
    }
  }

  toggleArr(key, v) {
    const arr = this.s[key];
    this.set({ [key]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  }
  togglePref(key, v) {
    const arr = this.s.prefs[key] || [];
    const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    this.set({ prefs: { ...this.s.prefs, [key]: next } });
  }
  displayName(p) {
    const b = (p && p.brand) || '';
    let n = (p && p.name) || '';
    if (b && n.toLowerCase().indexOf(b.toLowerCase()) === 0) n = n.slice(b.length).replace(/^[\s\-–|:]+/, '').trim();
    return n || (p && p.name) || '';
  }
  selectMachine(id) { this.set({ machine: this.data.machines.find((m) => String(m.id) === String(id)) || null }); }
  goNext() { this.set({ step: Math.min(this.s.step + 1, STEPS.length - 1) }); this.top(); }
  restart() { this.set({ step: 0, owned: null, machine: null, maskType: null, prefs: { cushion: [], headgear: [], brand: [] }, answers: {}, overlay: false, qStep: 0, sizes: {}, sizeError: {}, brandFilter: [], typeFilter: [], recommendation: null }); this.top(); }
  top() { try { this.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }

  quizNext() {
    const qs = this.data.questions;
    if (this.s.qStep < qs.length - 1) this.set({ qStep: this.s.qStep + 1 });
    else this.set({ qStep: qs.length }); // result screen
  }
  finishQuiz() {
    const rec = recommendMaskType(this.s.answers);
    this.set({ maskType: rec.type, recommendation: rec, overlay: false });
  }

  /* ---------- add to cart ---------- */
  async addMask(id) {
    const mask = this.data.masks.find((m) => String(m.id) === String(id));
    if (!mask) return;
    let variantId = this.s.sizes[id];
    if (!variantId) {
      if (mask.variants && mask.variants.length === 1) variantId = mask.variants[0].id;
      else { this.set({ sizeError: { ...this.s.sizeError, [id]: true } }); return; }
    }
    this.addToCart(variantId);
  }
  async addToCart(variantId) {
    if (!variantId) return;
    const root = (window.Theme && window.Theme.routes && window.Theme.routes.root) || '/';
    this.classList.add('is-loading');
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const json = await res.json();
      if (json && json.status) { this.toast(json.description || json.message || 'Could not add to cart'); return; }
      document.dispatchEvent(new CartAddEvent({}, 'cpap-mask-finder', { source: 'cpap-mask-finder' }));
      this.toast('Added to cart');
    } catch (e) {
      this.toast('Something went wrong — please try again');
    } finally {
      this.classList.remove('is-loading');
    }
  }
  toast(msg) {
    const el = this.querySelector('[data-toast]');
    if (!el) return;
    el.innerHTML = `<div class="toast" role="status">${ICON.checkSmall} ${esc(msg)}</div>`;
    clearTimeout(this._toastT);
    this._toastT = setTimeout(() => { el.innerHTML = ''; }, 2400);
  }

  /* ---------- derived ---------- */
  popularByType() {
    const out = {};
    MASK_TYPES.forEach((tp) => {
      const arr = this.data.masks.filter((m) => m.type === tp).slice();
      arr.sort((a, b) => this.popScore(b) - this.popScore(a));
      out[tp] = arr[0] || null;
    });
    return out;
  }
  popScore(m) {
    let s = (Number(m.rating) || 0) * (Number(m.reviews) || 0);
    if (m.salesRank) s += (1000 - Math.min(m.salesRank, 1000));
    return s;
  }

  /* ---------- render ---------- */
  render() {
    const s = this.s;
    const trust = this.data.settings.showTrustBar
      ? `<div class="trustbar"><div class="trustbar-inner">
          <span class="trustbar-item">${ICON.truck} Free shipping on orders over $200</span>
          <span class="trustbar-item">${ICON.shield} Lowest price guarantee</span>
          <span class="trustbar-item">${ICON.starOutline} Thousands of 5-star reviews</span>
          <span class="trustbar-item">${ICON.help} Australian customer support</span>
        </div></div>` : '';
    let body = '';
    if (s.step === 0) body = this.stepOwnership();
    else if (s.step === 1) body = this.stepMachine();
    else if (s.step === 2) body = this.stepMaskType();
    else if (s.step === 3) body = this.stepPrefs();
    else if (s.step === 4) body = this.stepResults();

    this.innerHTML = `<div class="app">${trust}${this.stepper()}<main class="canvas">${body}</main><div data-toast></div></div>${s.overlay ? this.questionnaire() : ''}`;
  }

  stepper() {
    const cur = this.s.step;
    let out = '<nav class="stepper" aria-label="Progress"><div class="stepper-inner">';
    STEPS.forEach((label, i) => {
      const cls = i === cur ? 'active' : i < cur ? 'done' : '';
      out += `<div class="step ${cls}"><span class="step-dot">${i < cur ? ICON.checkSmall : i + 1}</span><span class="step-label">${esc(label)}</span></div>`;
      if (i < STEPS.length - 1) out += '<span class="step-sep"></span>';
    });
    return out + '</div></nav>';
  }

  /* Step 0 */
  stepOwnership() {
    const s = this.s;
    const sel = (v) => (s.owned === v ? 'selected' : '');
    return `<section><div>
      <span class="hero-eyebrow">Mask Finder · 2 mins</span>
      <h1>Find the right CPAP mask <em>for the way you sleep</em>.</h1>
      <p class="hero-sub">A friendly, step-by-step guide that matches you to a mask based on your machine, how you breathe and how you sleep. Not sure? We'll help you choose.</p>
      <div class="hero-q"><span class="hero-q-num">1</span>Do you already have a CPAP machine?</div>
      <div class="hero-choices">
        <button class="choice-card ${sel(true)}" data-action="own" data-val="true" aria-pressed="${s.owned === true}">
          <div class="choice-card-icon">${ICON.shield}</div>
          <div class="choice-card-title">Yes, I have a machine</div>
          <div class="choice-card-sub">I just need a compatible mask — no machine purchase.</div>
          <div class="choice-card-meta">${ICON.checkSmall} Find a mask that pairs with my machine</div>
          <div class="choice-card-check">${ICON.check}</div>
        </button>
        <button class="choice-card ${sel(false)}" data-action="own" data-val="false" aria-pressed="${s.owned === false}">
          <div class="choice-card-icon">${ICON.cart}</div>
          <div class="choice-card-title">No, I'm shopping for a machine too</div>
          <div class="choice-card-sub">Build a machine + mask bundle and save on the mask.</div>
          <div class="choice-card-meta">${ICON.bundle} Bundle deal available</div>
          <div class="choice-card-check">${ICON.check}</div>
        </button>
      </div>
      <div class="hero-reassure">${ICON.help} Not sure? You can change this anytime as you go.</div>
    </div>
    ${this.sticky({ primary: 'Continue', primaryDisabled: s.owned === null, primaryAction: 'next', back: false })}</section>`;
  }

  /* Step 1 */
  machineType(m) { return m.type; }
  stepMachine() {
    const s = this.s, data = this.data;
    const filtered = data.machines.filter((m) =>
      (s.brandFilter.length === 0 || s.brandFilter.includes(m.brand)) &&
      (s.typeFilter.length === 0 || s.typeFilter.includes(m.type)));
    const byType = {};
    filtered.forEach((m) => { (byType[m.type] = byType[m.type] || []).push(m); });
    Object.values(byType).forEach((arr) => arr.sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0) || (b.rating - a.rating)));
    const groups = data.machineTypes.filter((tp) => byType[tp]).map((tp) => ({ type: tp, items: byType[tp] }));
    const hasFilter = s.brandFilter.length + s.typeFilter.length > 0;

    const chip = (label, action, active) => `<button class="chip ${active ? 'active' : ''}" data-action="${action}" data-val="${esc(label)}" aria-pressed="${active}">${esc(label)}${active ? '<span class="chip-x">×</span>' : ''}</button>`;

    let grid;
    if (filtered.length === 0) {
      grid = `<div class="empty-state">${ICON.alert}<h3>No machines match those filters</h3><p>Try removing a filter — or skip ahead and we'll show you every mask.</p><button class="btn btn-secondary" data-action="fclear">Clear filters</button></div>`;
    } else {
      grid = groups.map((g) => `<div class="machine-group"><div class="machine-group-title">${esc(g.type)} · ${g.items.length}</div><div class="machine-grid">${g.items.map((m) => this.machineCard(m)).join('')}</div></div>`).join('');
    }

    return `<section>
      <div class="step-header">
        <div><h2>${s.owned ? 'Which machine do you have?' : 'Choose your CPAP machine'}</h2>
        <p>${s.owned ? "Tell us your machine and we'll only show masks that fit it. No purchase needed here." : "Pick the machine you want, then we'll add a matching mask."}</p></div>
        <div class="step-header-meta"><span><b>${filtered.length}</b> of ${data.machines.length} machines</span>${hasFilter ? '<button class="link-btn" data-action="fclear">Clear filters</button>' : ''}</div>
      </div>
      <div class="filter-bar" role="group" aria-label="Filter machines">
        <div class="filter-row"><span class="chip-group-label">${ICON.filter} Brand</span>${data.brands.map((b) => chip(b, 'fbrand', s.brandFilter.includes(b))).join('')}</div>
        <div class="filter-row"><span class="chip-group-label">Type</span>${data.machineTypes.map((t) => chip(t, 'ftype', s.typeFilter.includes(t))).join('')}</div>
      </div>
      ${grid}
      <div class="skip-row"><p><b>Don't want to specify a machine?</b> Skip ahead — we'll show every mask.</p><button class="btn btn-secondary btn-sm" data-action="skip-machine">Skip — show me all masks</button></div>
      ${this.sticky({ primary: s.machine ? 'Continue with ' + this.displayName(s.machine) : 'Choose a machine', primaryDisabled: !s.machine, primaryAction: 'next', secondary: 'Skip', secondaryAction: 'skip-machine' })}
    </section>`;
  }
  machineCard(m) {
    const s = this.s;
    const selected = s.machine && String(s.machine.id) === String(m.id);
    const save = m.compareAt ? m.compareAt - m.price : 0;
    const cta = s.owned
      ? `<button class="btn btn-primary btn-sm" data-action="machine-select" data-id="${esc(m.id)}">${selected ? 'Selected' : 'Select this machine'}</button>
         <a class="btn btn-ghost btn-sm" href="${esc(m.url)}" target="_blank" rel="noopener">View details</a>`
      : `<button class="btn btn-primary btn-sm" data-action="machine-add" data-id="${esc(m.id)}" data-variant-id="${esc(m.variantId || '')}">${ICON.cart} Add to cart</button>
         <a class="btn btn-secondary btn-sm" href="${esc(m.url)}" target="_blank" rel="noopener">View details</a>`;
    return `<div class="prod-card ${selected ? 'selected' : ''}">
      <div class="prod-card-flags">${m.bestseller ? '<span class="badge badge-bestseller">Best seller</span>' : ''}${save > 0 ? `<span class="badge badge-sale">Save ${money(save)}</span>` : ''}</div>
      <div class="prod-card-select-marker">${selected ? ICON.check : ''}</div>
      <button class="prod-img" data-action="machine-select" data-id="${esc(m.id)}" style="border:0;width:100%;padding:0">${productImg(m, 'machine', '78%')}</button>
      <div class="prod-card-body">
        <div class="prod-card-brand">${esc(m.brand)}</div>
        <div class="prod-card-name">${esc(this.displayName(m))}</div>
        <div class="prod-card-tags"><span class="badge badge-type">${esc(m.type)}</span>${m.prescription ? '<span class="badge badge-type">Prescription</span>' : ''}</div>
        ${m.desc ? `<p class="prod-card-desc">${esc(m.desc)}</p>` : ''}
        <div class="prod-card-row"><div><span class="prod-card-price">${money(m.price)}</span>${m.compareAt ? `<span class="prod-card-price-compare">${money(m.compareAt)}</span>` : ''}</div>${m.rating ? stars(m.rating, m.reviews) : ''}</div>
        <div class="prod-card-cta-row">${cta}</div>
        ${!s.owned && m.bundle ? `<div class="prod-card-bundle-hint">${ICON.bundle} Add a mask & save on the bundle</div>` : ''}
      </div>
    </div>`;
  }

  /* Step 2 */
  stepMaskType() {
    const s = this.s;
    const rec = s.recommendation;
    const recType = rec && rec.type;
    const pop = this.popularByType();
    const TYPES = [
      { key: 'Full Face', tagline: 'Covers your nose and mouth. Best if you breathe through your mouth, get a blocked nose, or use a higher pressure.', pros: ['Seals even with congestion or mouth-breathing', 'Reliable at high pressures', "Won't dry out your mouth at night"], tradeoff: 'Largest footprint of the three. Some side sleepers prefer something smaller.' },
      { key: 'Nasal', tagline: 'Covers just your nose. A balanced option with a secure seal and a natural airflow feel.', pros: ['Smaller than a full face mask', 'Works at most prescribed pressures', 'Many find it the most natural to get used to'], tradeoff: 'Not ideal if you breathe through your mouth or have heavy congestion.' },
      { key: 'Nasal Pillow', tagline: 'Tiny cushions sit at your nostrils. Minimal contact — great for claustrophobia, side-sleeping, glasses or reading in bed.', pros: ['Lightest, least visible option', 'Open field of view for reading or TV', 'Easy for active and side sleepers'], tradeoff: 'Best at low-to-moderate pressures. Takes a night or two to get used to.' },
    ];
    const cards = TYPES.map((t) => {
      const selected = s.maskType === t.key;
      const recommended = recType === t.key;
      const p = pop[t.key];
      return `<div class="mask-type-card ${selected ? 'selected' : ''} ${recommended ? 'recommended' : ''}">
        <div class="mask-type-img">${recommended ? `<div class="mask-type-rec-flag"><span class="badge badge-recommended">${ICON.sparkle} Your match</span></div>` : ''}${placeholder(kindForType(t.key), '55%')}</div>
        <div class="mask-type-body">
          <div class="mask-type-name">${t.key} Mask</div>
          <div class="mask-type-tagline">${esc(t.tagline)}</div>
          <ul class="mask-type-pros">${t.pros.map((p2) => `<li>${ICON.checkSmall} ${esc(p2)}</li>`).join('')}</ul>
          <div class="mask-type-tradeoff"><b>Trade-off:</b> ${esc(t.tradeoff)}</div>
          <div class="mask-type-foot">
            <button class="btn ${selected ? 'btn-accent' : 'btn-primary'} btn-sm btn-block" data-action="masktype" data-type="${esc(t.key)}" aria-pressed="${selected}">${selected ? ICON.check + ' Selected' : 'Choose ' + t.key.toLowerCase()}</button>
            ${p ? `<div class="mask-type-mini">
              <div class="mask-type-mini-thumb">${productImg(p, kindForType(t.key), '78%')}</div>
              <div class="mask-type-mini-body"><div class="mask-type-mini-eyebrow">Most popular ${t.key.toLowerCase()}</div><div class="mask-type-mini-name">${esc(p.brand)} ${esc(this.displayName(p))}</div><div class="mask-type-mini-price">${money(p.price)}</div></div>
              <button class="btn btn-secondary btn-sm" data-action="add-mask" data-id="${esc(p.id)}" aria-label="Add ${esc(p.name)}">${ICON.cart}</button>
            </div>` : ''}
            <a class="link-btn" href="${esc(this.collectionUrl(t.key))}" target="_blank" rel="noopener" style="margin-top:10px;align-self:flex-start">Shop all ${t.key.toLowerCase()} masks →</a>
          </div>
        </div>
      </div>`;
    }).join('');

    return `<section>
      <div class="step-header"><div><h2>What type of mask suits you?</h2><p>Three styles, each suited to different sleepers. Tap to choose — or have us help you decide.</p></div></div>
      <div class="help-panel" role="region" aria-label="Help me choose">
        <div class="help-panel-icon">${ICON.sparkle}</div>
        <div class="help-panel-text"><h3>Not sure which is right for you?</h3><p>Answer a few quick questions about how you breathe and sleep — we'll match you to a mask type.</p></div>
        <button class="btn btn-accent" data-action="open-quiz">${rec ? 'Retake the quiz' : 'Help me choose'} ${ICON.arrowRight}</button>
      </div>
      <div class="mask-type-grid">${cards}</div>
      ${this.sticky({ primary: s.maskType ? 'Continue with ' + s.maskType.toLowerCase() : 'Pick a mask type', primaryDisabled: !s.maskType, primaryAction: 'next', secondary: 'Help me choose', secondaryAction: 'open-quiz' })}
    </section>`;
  }
  collectionUrl(type) {
    const map = this.data.settings.collectionUrls || {};
    return map[type] || '#';
  }

  /* Questionnaire overlay */
  questionnaire() {
    const qs = this.data.questions;
    const s = this.s;
    const isResult = s.qStep === qs.length;
    const q = !isResult ? qs[s.qStep] : null;
    const value = q ? s.answers[q.id] : null;
    const progress = isResult ? 100 : (s.qStep / qs.length) * 100;

    let bodyHtml;
    if (isResult) {
      const rec = recommendMaskType(s.answers);
      const r = rec.reasons.slice(0, 3);
      let why;
      if (r.length === 0) why = `Based on your answers, ${rec.headline.toLowerCase().replace('.', '')} — it should balance comfort and seal for you.`;
      else { const joined = r.length === 1 ? r[0] : r.slice(0, -1).join(', ') + ' and ' + r[r.length - 1]; why = `Because ${joined}, we recommend a ${rec.type.toLowerCase()} mask. It's a strong balance of seal, comfort and freedom of movement for your situation.`; }
      bodyHtml = `<div class="q-body">
        <div class="match-hero"><span class="match-eyebrow">Your match</span><div class="match-title">${esc(rec.headline)}</div></div>
        <div class="match-rec-card"><div class="match-rec-card-row">
          <div class="match-rec-thumb">${placeholder(kindForType(rec.type), '80%')}</div>
          <div class="match-rec-info"><div class="match-rec-flag"><span class="badge badge-recommended">${ICON.sparkle} Recommended</span></div>
          <div class="match-rec-name">${rec.type} Mask</div>
          <div class="match-rec-why"><b>Why this fits you:</b> ${esc(why)}</div></div>
        </div></div>
        <div class="match-alt"><div class="match-alt-icon">${placeholder(kindForType(rec.alt), '78%')}</div>
          <div class="match-alt-text"><div class="match-alt-eyebrow">Prefer ${rec.alt === 'Full Face' ? 'more' : 'less'} coverage? Also consider</div><div class="match-alt-name">${rec.alt} Mask</div></div>${ICON.chevRight}</div>
        ${s.answers.implant === 'yes' ? `<div class="q-caution" style="margin-top:14px">${ICON.alert}<span>Because you mentioned a metal implant, we'll filter out masks with magnetic clips on the next step. If unsure, please check with your clinician.</span></div>` : ''}
        <div class="match-reassure">These are suggestions — your clinician or sleep specialist can confirm what's right for you.</div>
      </div>`;
    } else {
      bodyHtml = `<div class="q-body">
        <div class="q-step-meta">Question ${s.qStep + 1} of ${qs.length}${q.isSafety ? ' · Safety check' : ''}</div>
        <h3 class="q-question">${esc(q.title)}</h3>
        <div class="q-options" role="radiogroup">${q.options.map((o) => `<button class="q-option ${value === o.v ? 'selected' : ''}" data-action="quiz-pick" data-q="${esc(q.id)}" data-val="${esc(o.v)}" role="radio" aria-checked="${value === o.v}"><span class="q-option-dot">${value === o.v ? ICON.checkSmall : ''}</span><span>${esc(o.label)}</span></button>`).join('')}</div>
        ${q.isSafety && value === 'yes' ? `<div class="q-caution">${ICON.alert}<span>Got it — we'll avoid masks with magnetic clips in your recommendations. Always confirm with your clinician.</span></div>` : ''}
      </div>`;
    }

    const foot = isResult
      ? `<button class="btn btn-ghost" data-action="quiz-back">${ICON.chevLeft} Back</button><button class="btn btn-primary" data-action="quiz-finish">Continue to masks ${ICON.arrowRight}</button>`
      : `<button class="btn btn-ghost" data-action="quiz-back" ${s.qStep === 0 ? 'disabled' : ''}>${ICON.chevLeft} Back</button><button class="btn btn-primary" data-action="quiz-next" ${!value ? 'disabled' : ''}>${s.qStep === qs.length - 1 ? 'See my match' : 'Next'} ${ICON.arrowRight}</button>`;

    return `<div class="q-overlay" role="dialog" aria-modal="true" aria-label="Help me choose a mask"><div class="q-modal">
      <div class="q-head"><span class="badge badge-bestseller">${ICON.sparkle} Help me choose</span><span class="q-head-title">${isResult ? 'Your match' : 'Question ' + (s.qStep + 1) + ' of ' + qs.length}</span><button class="q-head-close" data-action="close-quiz" aria-label="Close">${ICON.close}</button></div>
      <div class="q-progress"><div class="q-progress-fill" style="width:${progress}%"></div></div>
      ${bodyHtml}
      <div class="q-foot">${foot}</div>
    </div></div>`;
  }

  /* Step 3 */
  stepPrefs() {
    const s = this.s, data = this.data;
    const match = data.masks.filter((m) => {
      if (s.maskType && m.type !== s.maskType) return false;
      if (s.prefs.cushion.length && !s.prefs.cushion.includes(m.cushion)) return false;
      if (s.prefs.brand.length && !s.prefs.brand.includes(m.brand)) return false;
      return true;
    }).length;
    const opt = (key, v, note) => `<button class="pref-option ${s.prefs[key].includes(v) ? 'selected' : ''}" data-action="pref" data-key="${key}" data-val="${esc(v)}"><span>${esc(v)}${note ? `<span class="pref-option-note">${esc(note)}</span>` : ''}</span>${s.prefs[key].includes(v) ? ICON.checkSmall : ''}</button>`;
    return `<section>
      <div class="step-header"><div><h2>Any preferences for fit?</h2><p>Optional — these tighten your recommendations. Skip to see every ${s.maskType ? esc(s.maskType.toLowerCase()) : 'mask'} match.</p></div><div class="step-header-meta"><span><b>${match}</b> masks match so far</span></div></div>
      <div class="pref-card-grid">
        <div class="pref-card"><h4>Cushion material</h4><p class="pref-card-sub">Memory foam suits sensitive skin; silicone is easier to clean.</p><div class="pref-options">${opt('cushion', 'Silicone', 'Easy to clean · best value')}${opt('cushion', 'Memory foam', 'Soft on sensitive skin')}${opt('cushion', 'Gel', 'Cushioned feel')}</div></div>
        <div class="pref-card"><h4>Headgear style</h4><p class="pref-card-sub">Where the hose connects and how the straps sit.</p><div class="pref-options">${opt('headgear', 'Top-of-head tube', 'Open field of view')}${opt('headgear', 'Standard front-of-face', 'Most familiar')}${opt('headgear', 'Quick-release clips', 'Easy on/off')}</div></div>
        <div class="pref-card"><h4>Brand preference</h4><p class="pref-card-sub">Any brand is fine — pick a favourite if you have one.</p><div class="pref-options">${data.brands.map((b) => opt('brand', b)).join('')}</div></div>
      </div>
      ${this.sticky({ primary: `See ${match} matches`, primaryAction: 'next', secondary: 'Skip', secondaryAction: 'skip-prefs' })}
    </section>`;
  }

  /* Step 4 */
  typeReasons(type) {
    const a = this.s.answers, out = [];
    if (type === 'Full Face') {
      if (a.breathing === 'mouth' || a.breathing === 'dry-mouth' || a.congestion === 'often') out.push('seals through a blocked nose or mouth-breathing');
      if (a.pressure === 'high') out.push('holds a steady seal at higher pressures');
    } else if (type === 'Nasal Pillow') {
      if (a.claustro === 'yes' || a.claustro === 'abit') out.push('minimal-contact design');
      if (a.position === 'side' || a.active === 'yes') out.push('easy for side and active sleepers');
      if (a.readers === 'yes') out.push('open field of view for reading or glasses');
    } else if (type === 'Nasal') {
      if (a.readers === 'yes') out.push('open field of view');
      out.push('a natural balance of seal and comfort');
    }
    return out;
  }
  scoreMasks() {
    const s = this.s, data = this.data;
    let masks = data.masks.filter((m) => (s.maskType ? m.type === s.maskType : true));
    if (s.prefs.cushion.length) masks = masks.filter((m) => s.prefs.cushion.includes(m.cushion));
    if (s.prefs.brand.length) masks = masks.filter((m) => s.prefs.brand.includes(m.brand));
    if (s.machine && Array.isArray(s.machine.compatibleMasks) && s.machine.compatibleMasks.length) {
      const ok = new Set(s.machine.compatibleMasks.map(String));
      masks = masks.filter((m) => ok.has(String(m.id)));
    }
    if (s.answers.implant === 'yes') masks = masks.filter((m) => !m.magneticClip);

    const baseReasons = s.maskType ? this.typeReasons(s.maskType) : [];
    masks = masks.map((m) => {
      let score = (Number(m.rating) || 0) * 2 + Math.log10((Number(m.reviews) || 0) + 1);
      if (m.salesRank) score += (100 - Math.min(m.salesRank, 100)) / 20;
      const why = baseReasons.slice();
      if (s.answers.skin === 'yes' && m.cushion === 'Memory foam') { score += 1.5; why.unshift('memory-foam cushion is kind on sensitive skin'); }
      if (m.compareAt) score += 0.2;
      return Object.assign({}, m, { _score: score, _why: why });
    });
    masks.sort((a, b) => b._score - a._score);
    return masks;
  }
  stepResults() {
    const s = this.s;
    const masks = this.scoreMasks();
    const top = masks.slice(0, 6);
    const others = masks.slice(6, 9);
    const recapMachine = s.machine ? `<div class="recs-recap-line">Machine: <b>${esc(s.machine.brand)} ${esc(s.machine.name)}</b></div>` : (!s.owned && !s.machine ? '<div class="recs-recap-line">Shopping for: <b>Mask only</b></div>' : '');
    const recap = `<div class="recs-recap"><div class="recs-recap-title">Matched for you</div>
      <div class="recs-recap-line">Mask type: <b>${esc(s.maskType || 'Any')}</b></div>${recapMachine}
      ${s.prefs.cushion.length ? `<div class="recs-recap-line">Cushion: <b>${esc(s.prefs.cushion.join(', '))}</b></div>` : ''}
      ${s.prefs.brand.length ? `<div class="recs-recap-line">Brand: <b>${esc(s.prefs.brand.join(', '))}</b></div>` : ''}</div>`;
    const trust = `<div style="padding:18px 22px;border:1px solid #eee;border-radius:10px;background:var(--white);display:flex;flex-direction:column;gap:10px">
      <div style="font-size:12px;font-weight:600;color:var(--grey-500);text-transform:uppercase;letter-spacing:0.08em">Why shop with us</div>
      <div style="display:flex;align-items:center;gap:10px;font-size:13.5px;color:var(--grey-700)"><span style="color:var(--navy)">${ICON.truck}</span>Free shipping over $200</div>
      <div style="display:flex;align-items:center;gap:10px;font-size:13.5px;color:var(--grey-700)"><span style="color:var(--navy)">${ICON.shield}</span>Lowest price guarantee</div>
      <div style="display:flex;align-items:center;gap:10px;font-size:13.5px;color:var(--grey-700)"><span style="color:var(--navy)">${ICON.starOutline}</span>Thousands of 5-star reviews</div>
      ${s.answers.implant === 'yes' ? `<div style="margin-top:4px;font-size:12px;color:var(--warn);display:flex;gap:8px;align-items:flex-start">${ICON.alert}<span>We've filtered out masks with magnetic clips because of your implant note.</span></div>` : ''}</div>`;

    const grid = masks.length === 0
      ? `<div class="empty-state">${ICON.alert}<h3>No exact matches — here are the closest options</h3><p>Loosening one of your preferences usually surfaces a great fit.</p><button class="btn btn-secondary" data-action="restart">Adjust my answers</button></div>`
      : `<div class="mask-grid">${top.map((m, i) => this.maskCard(m, i + 1, i === 0, false)).join('')}</div>`;

    const bundle = (!s.owned && s.machine)
      ? `<div class="bundle-strip" role="region" aria-label="Bundle offer"><div class="bundle-strip-icon">${ICON.bundle}</div><div class="bundle-strip-text"><h4>Add a mask to your ${esc(s.machine.name)} and save on the bundle</h4><p>Bundle savings apply at checkout. Free shipping included.</p></div><a class="btn btn-accent" href="${esc(s.machine.url)}" target="_blank" rel="noopener">See the bundle</a></div>` : '';

    const isAirMini = s.machine && /airmini/i.test(s.machine.name || '');
    const airminiNote = isAirMini
      ? `<div class="bundle-strip" role="region" aria-label="AirMini compatibility"><div class="bundle-strip-icon">${ICON.alert}</div><div class="bundle-strip-text"><h4>Using the ResMed AirMini?</h4><p>The AirMini is a travel machine with its own setup — most masks need a ResMed setup pack (with HumidX waterless humidification) or a hose adapter to connect.</p></div><a class="btn btn-accent" href="/search?q=AirMini%20setup%20pack" target="_blank" rel="noopener">AirMini setup &amp; adapters</a></div>` : '';

    const othersHtml = others.length ? `<div class="crosssell"><h3>Other options you might consider <span class="badge">${others.length}</span></h3><div class="mask-grid">${others.map((m, i) => this.maskCard(m, top.length + i + 1, false, true)).join('')}</div></div>` : '';

    const acc = (this.data.accessories || []).slice(0, 4);
    const accHtml = acc.length ? `<div class="crosssell"><h3>Don't forget <span class="badge badge-accessory">Accessories</span></h3><div class="crosssell-row">${acc.map((a) => `<div class="xs-card"><div class="xs-thumb">${productImg(a, 'accessory', '80%')}</div><div class="xs-body"><div class="xs-name">${esc(a.title)}</div><div class="xs-price">${money(a.price)}</div></div><button class="xs-add" aria-label="Add ${esc(a.title)}" data-action="add-cart" data-variant-id="${esc(a.variantId || '')}">+</button></div>`).join('')}</div></div>` : '';

    return `<section>
      <div class="step-header"><div><h2>Your top mask matches</h2><p>Ranked best-fit first, based on your answers. Most ${s.maskType ? esc(s.maskType.toLowerCase()) : 'mask'} wearers settle in within 1–2 nights.</p></div><div class="step-header-meta"><span><b>${masks.length}</b> matches</span><button class="link-btn" data-action="restart">${ICON.refresh} Start over</button></div></div>
      <div class="recs-head">${recap}${trust}</div>
      ${airminiNote}
      ${grid}
      ${bundle}
      ${othersHtml}
      ${accHtml}
      ${this.sticky({ back: true, secondary: 'Start over', secondaryAction: 'restart' })}
    </section>`;
  }
  maskCard(m, rank, isTop, compact) {
    const s = this.s;
    const save = m.compareAt ? m.compareAt - m.price : 0;
    const selectedVariant = this.s.sizes[m.id];
    const flags = `${isTop ? `<span class="badge badge-recommended">${ICON.sparkle} Top match</span>` : ''}${save > 0 ? `<span class="badge badge-sale">Save ${money(save)}</span>` : ''}${m.cushion === 'Memory foam' ? '<span class="badge">Memory foam</span>' : ''}`;
    const sizePills = (m.variants || []).map((v) => `<button class="size-pill ${selectedVariant === String(v.id) || selectedVariant === v.id ? 'selected' : ''} ${v.available ? '' : 'disabled'}" data-action="size" data-id="${esc(m.id)}" data-variant-id="${esc(v.id)}" ${v.available ? '' : 'disabled'} role="radio" aria-checked="${selectedVariant == v.id}">${esc(v.title)}</button>`).join('');
    const why = (m._why && m._why.length && !compact) ? `<div class="fit-why"><b>Why this fits you:</b> ${esc(this.personalisedWhy(m))}</div>` : '';
    const promos = !compact ? `<div class="promo-stack">
        ${(!s.owned && s.machine) ? `<div class="promo promo-bundle">${ICON.bundle} <span><b>Bundle:</b> Add to your <b>${esc(s.machine.name)}</b> and save</span></div>` : ''}
        ${save > 0 ? `<div class="promo promo-sale">${ICON.tag} <span><b>On sale</b> — ${money(save)} off</span></div>` : ''}
        <div class="promo promo-accessory">${ICON.bundle} <span>Save 15% on a <b>cushion 3-pack</b> with this mask</span></div>
      </div>` : '';
    const sizeErr = this.s.sizeError[m.id] ? '<div style="font-size:12.5px;color:var(--danger)">Please choose a size first</div>' : '';
    return `<div class="mask-card ${isTop ? 'top-match' : ''}">
      <div class="mask-img"><div class="mask-flags">${flags}</div><div class="mask-rank">${rank}</div>${productImg(m, kindForType(m.type), '60%')}</div>
      <div class="mask-body">
        <div class="mask-brand-row"><span class="mask-brand">${esc(m.brand)}</span>${m.rating ? stars(m.rating, m.reviews) : ''}</div>
        <div class="mask-name">${esc(this.displayName(m))}</div>
        <div class="mask-price-row"><span class="mask-price">${money(m.price)}</span>${m.compareAt ? `<span class="mask-price-compare">${money(m.compareAt)}</span>` : ''}${save > 0 ? `<span class="mask-save">Save ${money(save)}</span>` : ''}</div>
        ${why}
        ${(m.variants && m.variants.length > 1) ? `<div class="size-row"><span class="size-label">Size</span><div class="size-pills" role="radiogroup" aria-label="Size">${sizePills}</div>${m.sizingGuide ? `<a class="size-guide-link" href="${esc(m.sizingGuide)}" target="_blank" rel="noopener">Sizing guide →</a>` : ''}</div>` : (m.sizingGuide ? `<div class="size-row"><a class="size-guide-link" href="${esc(m.sizingGuide)}" target="_blank" rel="noopener" style="margin-left:0">Sizing guide →</a></div>` : '')}
        ${sizeErr}
        ${promos}
        <div class="mask-cta-row"><button class="btn btn-primary" data-action="add-mask" data-id="${esc(m.id)}">${ICON.cart} Add to cart</button><a class="btn btn-secondary" href="${esc(m.url)}" target="_blank" rel="noopener">View details</a></div>
      </div>
    </div>`;
  }
  personalisedWhy(m) {
    const reasons = m._why || [];
    if (reasons.length === 0) return 'A reliable all-rounder in the ' + m.type.toLowerCase() + ' category.';
    if (reasons.length === 1) return 'A ' + m.type.toLowerCase() + ' mask with ' + reasons[0] + '.';
    return 'A ' + m.type.toLowerCase() + ' mask — ' + reasons.slice(0, 2).join(', and ') + '.';
  }

  sticky({ primary, primaryDisabled, primaryAction, secondary, secondaryAction, back = true }) {
    const backBtn = back ? `<button class="btn btn-ghost mf-bar-back" data-action="back">${ICON.chevLeft} Back</button>` : '<span></span>';
    const sec = secondary ? `<button class="btn btn-secondary" data-action="${secondaryAction}">${esc(secondary)}</button>` : '';
    const prim = primary ? `<button class="btn btn-primary" data-action="${primaryAction}" ${primaryDisabled ? 'disabled' : ''}>${esc(primary)}</button>` : '';
    return `<div class="sticky-mobile"><div class="sticky-mobile-inner">${backBtn}<div class="sticky-mobile-right">${sec}${prim}</div></div></div>`;
  }
}

if (!customElements.get('cpap-mask-finder')) customElements.define('cpap-mask-finder', CpapMaskFinder);
